import type { Warning } from '@/components/common/WarningIcon'
import type { VaultV2WarningFields, VaultWarningFields } from '@/types/fields'

import {
  type ApiVaultWarningWithEnumLevel,
  getVaultV2WarningMessage,
  getVaultWarningMessage,
  getVaultWarningScrollType,
  type UnifiedVaultWarning
} from '@/helpers/warnings'

/**
 * Aggregates a V1 vault's own warnings. Market warnings do not propagate to
 * vault surfaces; they render on market surfaces only.
 * @internal
 */
export function aggregateVaultWarnings(vaultWarnings: VaultWarningFields[] | undefined) {
  const warnings: Warning[] = []

  vaultWarnings?.forEach(warning => {
    const message = getVaultWarningMessage(warning)
    if (message) {
      warnings.push({
        level: warning.level,
        message,
        warningType: getVaultWarningScrollType(warning.type)
      })
    }
  })

  return warnings
}

/**
 * Aggregates a V2 vault's own warnings. Underlying markets and V1 adapters do
 * not propagate here.
 */
export function aggregateVaultV2Warnings(
  vaultV2Warnings:
    | (UnifiedVaultWarning | VaultV2WarningFields | ApiVaultWarningWithEnumLevel)[]
    | undefined
) {
  const warnings: Warning[] = []

  vaultV2Warnings?.forEach(warning => {
    // Consumer-API rows are pre-aggregated across sources; only the vault's own
    // rows belong here. GraphQL rows carry no `source` and always pass.
    if ('source' in warning && warning.source !== 'vaultV2') return

    const message = getVaultV2WarningMessage(warning)
    if (message) {
      warnings.push({
        level: warning.level,
        message,
        warningType: getVaultWarningScrollType(warning.type)
      })
    }
  })

  return warnings
}
